/**
 * Typed client for the NallaSMP control Worker API.
 *
 * All requests use `credentials: 'include'` so the HttpOnly session cookie
 * (`nallasmp.sid`) is transmitted cross-origin to the Worker. Mutations attach
 * the per-session CSRF token via the `x-csrf-token` header; the Worker validates
 * it and the Origin.
 */
import { WORKER_API_URL } from './config';

export interface SessionInfo {
  authenticated: boolean;
  user: string | null;
  role: 'admin' | 'viewer' | null;
  csrfToken: string | null;
}

export interface LoginResult {
  success: boolean;
  user?: string;
  role?: 'admin' | 'viewer';
  csrfToken?: string;
  error?: string;
}

export interface StatusResult {
  success: boolean;
  state: string;
  instanceId: string;
  region: string;
  lastUpdated: string;
  stateTransitionReason?: string;
  error?: string;
  errorCode?: string;
}

export interface ActionResult {
  success: boolean;
  state: string;
  detail?: string;
  error?: string;
  errorCode?: string;
}

export interface HandoffResult {
  success: boolean;
  ssoEnabled: boolean;
  token?: string;
  ttl?: number;
  error?: string;
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown; csrfToken?: string } = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.csrfToken) headers['x-csrf-token'] = opts.csrfToken;
  const resp = await fetch(`${WORKER_API_URL}/api${path}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const data = (await resp.json().catch(() => ({}))) as T;
  return { ok: resp.ok, status: resp.status, data };
}

export const getSession = () => request<SessionInfo>('/session');
export const login = (user: string, password: string) =>
  request<LoginResult>('/login', { method: 'POST', body: { user, password } });
export const logout = () => request<{ success: boolean }>('/logout', { method: 'POST' });
export const getStatus = () => request<StatusResult>('/status');
export const startInstance = (csrfToken: string) =>
  request<ActionResult>('/start', { method: 'POST', csrfToken });
export const stopInstance = (csrfToken: string) =>
  request<ActionResult>('/stop', { method: 'POST', csrfToken });

/**
 * Requests an SSO handoff token from the Worker (POST /api/sso/handoff).
 * Requires the session cookie (credentials: 'include') and the per-session
 * CSRF token. Returns { success, ssoEnabled, token } — when ssoEnabled is
 * false the caller should fall back to opening the canonical panel URL.
 */
export const requestHandoff = (csrfToken: string) =>
  request<HandoffResult>('/sso/handoff', { method: 'POST', csrfToken });
